/**
 * Slovník osobního odběru (KAN-2015, vlastník 2026-10-12).
 *
 * Osobní odběr se nikam neveze, a přesto ho admin i zákazník četli jako
 * zásilku: „Vytvořte a odešlete zásilku", „Vytvořit zásilku", „Kompletně
 * odesláno", u zákazníka „Odesláno" hned nad větou „Tuhle objednávku nikam
 * neposíláme". Stavy v datech zůstávají stejné (zásilka ``packed`` →
 * ``shipped`` → ``delivered``, viz ``services/personal_pickup``); u odběru
 * se jen čtou jinými slovy:
 *
 *   Připravit k vyzvednutí → Připraveno k vyzvednutí → Vydáno
 *
 * Jedna mapa pro admin (detail objednávky, terminál dílny) i zákazníka, aby
 * se slovník nerozešel. Volající předá klíč, jak ho zná pro zásilku, a
 * dostane klíč pro odběr, existuje-li; jinak týž klíč. Nikde „zásilka" ani
 * „odesláno".
 */

/** Klíče ``admin`` → jejich podoba u odběru (``admin:shipments.pickupVocab.*``). */
const ADMIN: Record<string, string> = {
  "shipments.heading": "shipments.pickupVocab.heading",
  "shipments.create": "shipments.pickupVocab.create",
  "shipments.existing": "shipments.pickupVocab.existing",
  "shipments.none": "shipments.pickupVocab.none",
  "shipments.noRemaining": "shipments.pickupVocab.noRemaining",
  "shipments.coverage.shipped": "shipments.pickupVocab.coverageShipped",
  "shipments.coverage.pack": "shipments.pickupVocab.coveragePack",
  "shipments.fulfillment.unshipped": "shipments.pickupVocab.fulfillment.unshipped",
  "shipments.fulfillment.partial": "shipments.pickupVocab.fulfillment.partial",
  "shipments.fulfillment.fulfilled": "shipments.pickupVocab.fulfillment.fulfilled",
  "shipments.status.packed": "shipments.pickupStatus.packed",
  "shipments.status.shipped": "shipments.pickupStatus.shipped",
  "shipments.status.delivered": "shipments.pickupStatus.delivered",
  "shipments.status.cancelled": "shipments.pickupStatus.cancelled",
  "shipments.markShipped": "shipments.markReadyForPickup",
  "shipments.markDelivered": "shipments.markPickedUp",
  // Detail objednávky, „Co teď" (WhatNowBanner).
  "orderDetail.whatNow.next.ship": "orderDetail.whatNow.nextPickup.ship",
  "orderDetail.whatNow.next.await_delivery": "orderDetail.whatNow.nextPickup.await_delivery",
  "orderDetail.whatNow.next.delivered": "orderDetail.whatNow.nextPickup.delivered",
};

/** Klíče ``order`` (zákazník) → jejich podoba u odběru (``order:shipments.pickupVocab.*``). */
const CUSTOMER: Record<string, string> = {
  "shipments.heading": "shipments.pickupVocab.heading",
  "shipments.status.packed": "shipments.pickupVocab.status.packed",
  "shipments.status.shipped": "shipments.pickupVocab.status.shipped",
  "shipments.status.delivered": "shipments.pickupVocab.status.delivered",
  "shipments.fulfillment.partial": "shipments.pickupVocab.fulfillment.partial",
  "shipments.fulfillment.fulfilled": "shipments.pickupVocab.fulfillment.fulfilled",
  "shipments.dispatch.shipped": "shipments.pickupVocab.dispatch.shipped",
  "shipments.dispatch.restLater": "shipments.pickupVocab.dispatch.restLater",
  "shipments.dispatch.remakeEta": "shipments.pickupVocab.dispatch.remakeEta",
  "shipments.dispatch.remakeNoEta": "shipments.pickupVocab.dispatch.remakeNoEta",
  // KAN-2101 (P41) — děkovačka: odběr se nedoručuje, připraví se.
  estimatedDelivery: "estimatedPickupReady",
};

/** Klíče ``track`` (stav objednávky u zákazníka, /track i detail objednávky). */
const TRACK: Record<string, string> = {
  "status.ready_for_packing": "statusPickup.ready_for_packing",
  "status.ready_to_ship": "statusPickup.ready_to_ship",
  "status.shipped": "statusPickup.shipped",
  "status.delivered": "statusPickup.delivered",
  "result.shipped": "resultPickup.shipped",
  "result.delivered": "resultPickup.delivered",
  "result.shipments": "resultPickup.shipments",
  "result.estimatedDelivery": "resultPickup.estimatedDelivery",
};

export type PickupAudience = "admin" | "customer" | "track";

/**
 * Klíč i18n pro danou věc: u osobního odběru jeho slovo, jinak beze změny.
 *
 * ``pickupKey("shipments.create", true)`` → ``"shipments.pickupVocab.create"``
 * („Připravit k vyzvednutí"); ``pickupKey("shipments.create", false)`` →
 * ``"shipments.create"`` („Vytvořit zásilku").
 */
export function pickupKey(
  key: string,
  pickup: boolean,
  audience: PickupAudience = "admin",
): string {
  if (!pickup) return key;
  const map = audience === "admin" ? ADMIN : audience === "track" ? TRACK : CUSTOMER;
  return map[key] ?? key;
}

/** Je objednávka osobní odběr? Jedno čtení pro všechny obrazovky. */
export function isPersonalPickup(order: {
  delivery_method?: string | null;
  personal_pickup?: boolean | null;
}): boolean {
  return order.personal_pickup === true || order.delivery_method === "pickup";
}
